import React, { useState, useEffect, useMemo } from "react";
import {
  Grid,
  Paper,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  Chip,
  Box,
  SelectChangeEvent,
  OutlinedInput,
  CircularProgress,
  Typography,
  Autocomplete,
} from "@mui/material";
import { vehicleExpenseApi } from "../../vehicleExpense/apiService";
import CloseIcon from "@mui/icons-material/Close";
import { useReportFilter } from "../context/ReportFilterContext";
import { Project } from "../../../project/project_module/types";
import {
  PetrolPump,
  petrolPumpApi,
  PickupMan,
  pickupManApi,
  ServiceOperator,
  serviceOperatorApi,
} from "../../../services/api";
import { projectApi } from "../../../project/project_module/services/apiService";
import { routeServiceApi } from "../../masterData/route/apiService";
import { commonTextFieldProps, smallButtonProp } from "../../../utils/styles";
import MultiSearchableAutocomplete from "../../../components/MultiSearchableAutoComplete";
import { PROJECT_TERMINOLOGY } from "../../../project/project_module/enums";
import { Party } from "../../masterData/party/types";
import { partyApi } from "../../masterData/party/apiService";
import {
  formatDate,
  formatDateRange,
  getDateRangeForFilter,
  TimeFilter,
} from "../../../utils/utils";

interface Vehicle {
  id: string;
  vehicleNumber: string;
  acquisitionType: string;
}

interface ReportFilterProps {
  showVehicleFilter?: boolean;
  showLocationFilter?: boolean;
  showOperatorFilter?: boolean;
  showMechanicFilter?: boolean;
  showGarageFilter?: boolean;
  showPickupManFilter?: boolean;
  showProviderFilter?: boolean;
  showDriverCleanerFilter?: boolean;
  showVehicleIdFilter?: boolean;
  showProviderIdFilter?: boolean;
  showProjectIdFilter?: boolean;
  showPartyIdFilter?: boolean;
}

const ReportFilter: React.FC<ReportFilterProps> = ({
  showVehicleFilter = false,
  showLocationFilter = false,
  showOperatorFilter = false,
  showPickupManFilter = false,
  showMechanicFilter = false,
  showGarageFilter = false,
  showProviderFilter = false,
  showDriverCleanerFilter = false,
  showVehicleIdFilter = false,
  showProviderIdFilter = false,
  showProjectIdFilter = false,
  showPartyIdFilter = false,
}) => {
  const {
    filterState,
    updateTimeFilter,
    updateDateRange,
    updateVehicles,
    updateLocations,
    updateOperator,
    updateMechanic,
    updateGarage,
    clearFilters,
    updateProvider,
    updateDriverCleaner,
    updateVehicleIds,
    updateProviderIds,
    updateProjectIds,
    updateParties,
  } = useReportFilter();

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [providers, setProviders] = useState<PetrolPump[]>([]);
  const [searchText, setSearchText] = useState("");
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [uniqueStops, setUniqueStops] = useState<
    {
      stopName: string;
    }[]
  >([{ stopName: "All" }]);
  const [parties, setParties] = useState<Party[]>([]);

  // Local state for all filter values
  const [localTimeFilter, setLocalTimeFilter] = useState<TimeFilter>(
    filterState.timeFilter
  );
  const [localCustomStartDate, setLocalCustomStartDate] = useState(
    filterState.customStartDate
  );
  const [localCustomEndDate, setLocalCustomEndDate] = useState(
    filterState.customEndDate
  );
  const [localSelectedVehicles, setLocalSelectedVehicles] = useState<string[]>(
    filterState.selectedVehicles
  );
  const [localPickupLocation, setLocalPickupLocation] = useState(
    filterState.pickupLocation || "All"
  );
  const [localDropLocation, setLocalDropLocation] = useState(
    filterState.dropLocation || "All"
  );
  const [localOperatorNames, setLocalOperatorNames] = useState(
    filterState.operatorNames
  );

  const [localMechanicName, setLocalMechanicName] = useState(
    filterState.mechanicName
  );

  const [localGarageName, setLocalGarageName] = useState(
    filterState.garageName
  );

  const [localProviderName, setLocalProviderName] = useState(
    filterState.providerName
  );

  const [localDriverCleanerName, setLocalDriverCleanerName] = useState(
    filterState.driverCleanerName
  );

  const [localSelectedVehicleIds, setLocalSelectedVehicleIds] = useState<
    string[]
  >(filterState.selectedVehicleIds);

  const [localSelectedProviderIds, setLocalSelectedProviderIds] = useState<
    string[]
  >(filterState.selectedProviderIds);

  const [localSelectedProjectIds, setLocalSelectedProjectIds] = useState<
    string[]
  >(filterState.selectedProjectIds);

  const [localSelectedParties, setLocalSelectedParties] = useState<string[]>(
    filterState.selectedParties
  );

  // Update local state when filter state changes from external sources
  useEffect(() => {
    setLocalTimeFilter(filterState.timeFilter);
    setLocalCustomStartDate(filterState.customStartDate);
    setLocalCustomEndDate(filterState.customEndDate);
    setLocalSelectedVehicles(filterState.selectedVehicles);
    setLocalPickupLocation(filterState.pickupLocation);
    setLocalDropLocation(filterState.dropLocation);
    setLocalOperatorNames(filterState.operatorNames);
    setLocalMechanicName(filterState.mechanicName);
    setLocalGarageName(filterState.garageName);
    setLocalProviderName(filterState.providerName);
    setLocalDriverCleanerName(filterState.driverCleanerName);
    setLocalSelectedVehicleIds(filterState.selectedVehicleIds);
    setLocalSelectedProviderIds(filterState.selectedProviderIds);
    setLocalSelectedProjectIds(filterState.selectedProjectIds);
    setLocalSelectedParties(filterState.selectedParties);
    // Reset loading state when filter state updates (after API call completes)
    if (isApplyingFilters) {
      setTimeout(() => setIsApplyingFilters(false), 100);
    }
  }, [filterState, isApplyingFilters]);

  useEffect(() => {
    if (showVehicleFilter || showVehicleIdFilter) {
      const fetchVehicles = async () => {
        try {
          const vehicleData = await vehicleExpenseApi.fetchVehicles();
          setVehicles(vehicleData);
        } catch (err) {
          console.error("Error fetching vehicles:", err);
        }
      };
      fetchVehicles();
    }
  }, [showVehicleFilter, showVehicleIdFilter]);

  useEffect(() => {
    if (showProviderIdFilter) {
      const fetchProviders = async () => {
        try {
          const providerData = await petrolPumpApi.search("");
          setProviders(providerData);
        } catch (err) {
          console.error("Error fetching providers:", err);
        }
      };
      fetchProviders();
    }
  }, [showProviderIdFilter]);

  useEffect(() => {
    if (showProjectIdFilter) {
      const fetchProjects = async () => {
        try {
          const projectData = await projectApi.getProjects();
          setProjects(projectData);
        } catch (err) {
          console.error(`Error fetching ${PROJECT_TERMINOLOGY.PLURAL}:`, err);
        }
      };
      fetchProjects();
    }
  }, [showProjectIdFilter]);

  useEffect(() => {
    if (showLocationFilter) {
      const fetchUniqueStops = async () => {
        const uniqueStops = await routeServiceApi.getAllUniqueStops();
        setUniqueStops([{ stopName: "All" }, ...uniqueStops]);
      };
      fetchUniqueStops();
    }
  }, [showLocationFilter]);

  useEffect(() => {
    if (showPartyIdFilter) {
      const fetchParties = async () => {
        const parties = await partyApi.getParties();
        setParties(parties);
      };
      fetchParties();
    }
  }, [showPartyIdFilter]);
  
  const handleRemoveVehicle = (vehicleToRemove: string) => {
    setLocalSelectedVehicles((prev) =>
      prev.filter((v) => v !== vehicleToRemove)
    );
  };

  const handleRemoveVehicleId = (vehicleIdToRemove: string) => {
    setLocalSelectedVehicleIds((prev) =>
      prev.filter((v) => v !== vehicleIdToRemove)
    );
  };

  // Function to get vehicle number by ID
  const getVehicleNumberById = (vehicleId: string) => {
    const vehicle = vehicles.find((v) => v.id === vehicleId);
    return vehicle?.vehicleNumber || vehicleId;
  };

  const getProjectNameById = (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    return project?.name || projectId;
  };

  const getProviderNameById = (providerId: string) => {
    const provider = providers.find((p) => p.id === providerId);
    return provider?.petrolPumpName || providerId;
  };

  const getPartyNameById = (partyId: string) => {
    const party = parties.find((p) => p.id === partyId);
    return party?.partyName || partyId;
  };

  const handleApplyFilters = () => {
    setIsApplyingFilters(true);
    updateTimeFilter(localTimeFilter);
    if (localTimeFilter === "custom") {
      updateDateRange(localCustomStartDate, localCustomEndDate);
    }
    updateVehicles(localSelectedVehicles);
    updateLocations(localPickupLocation, localDropLocation);
    updateOperator(localOperatorNames);
    updateMechanic(localMechanicName);
    updateGarage(localGarageName);
    updateProvider(localProviderName);
    updateDriverCleaner(localDriverCleanerName);
    updateVehicleIds(localSelectedVehicleIds);
    updateProviderIds(localSelectedProviderIds);
    updateProjectIds(localSelectedProjectIds);
    updateParties(localSelectedParties);
  };

  const handleClearFilters = () => {
    setSearchText("");
    setLocalTimeFilter("today");
    setLocalCustomStartDate("");
    setLocalCustomEndDate("");
    setLocalSelectedVehicles([]);
    setLocalPickupLocation("All");
    setLocalDropLocation("All");
    setLocalOperatorNames([]);
    setLocalMechanicName([]);
    setLocalGarageName([]);
    setLocalProviderName("");
    setLocalDriverCleanerName("");
    setLocalSelectedVehicleIds([]);
    setLocalSelectedProviderIds([]);
    setLocalSelectedProjectIds([]);
    setLocalSelectedParties([]);
    clearFilters();
  };

  const filteredVehicles = useMemo(
    () =>
      vehicles.filter((vehicle) =>
        vehicle.vehicleNumber.toLowerCase().includes(searchText.toLowerCase())
      ),
    [vehicles, searchText]
  );

  const getOptionLabel = (option: any) => {
    if (typeof option === "string") return option;
    return option.vehicleNumber;
  };

  const renderOptionContent = (option: any) => {
    if (typeof option === "string") return option;
    return option.vehicleNumber;
  };

  const getLocationOption = (location: any) => {
    if (typeof location === "string") return location;
    return location?.stopName || "";
  };

  const handleOperatorChange = (value: string | string[]) => {
    setLocalOperatorNames(typeof value === "string" ? value.split(",") : value);
  };

  const handleRemoveOperator = (operatorName: string) => {
    setLocalOperatorNames((prev) => prev.filter((v) => v !== operatorName));
  };

  const handleMechanicChange = (value: string | string[]) => {
    setLocalMechanicName(typeof value === "string" ? value.split(",") : value);
  };

  const handleGarageChange = (value: string | string[]) => {
    setLocalGarageName(typeof value === "string" ? value.split(",") : value);
  };
  return (
    <Paper sx={{ p: 1.5 }}>
      <Typography
        variant="h6"
        sx={{
          fontSize: "0.75rem",
          mt: "-10px",
          mb: 1,
        }}
      >
        Filters
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: "150px" },
            }}
          >
            <FormControl fullWidth size="small" sx={{ fontSize: "0.65rem" }}>
              <InputLabel sx={{ fontSize: "0.65rem" }}>Time Period</InputLabel>
              <Select
                value={localTimeFilter}
                label="Time Period"
                onChange={(e) =>
                  setLocalTimeFilter(e.target.value as TimeFilter)
                }
                sx={{ fontSize: "0.65rem" }}
              >
                <MenuItem value="today" sx={{ fontSize: "0.65rem" }}>
                  Today ({formatDate(getDateRangeForFilter("today").start)})
                </MenuItem>
                <MenuItem value="thisWeek" sx={{ fontSize: "0.65rem" }}>
                  This Week{" "}
                  {formatDateRange(
                    getDateRangeForFilter("thisWeek").start,
                    getDateRangeForFilter("thisWeek").end
                  )}
                </MenuItem>
                <MenuItem value="last7days" sx={{ fontSize: "0.65rem" }}>
                  Last 7 Days{" "}
                  {formatDateRange(
                    getDateRangeForFilter("last7days").start,
                    getDateRangeForFilter("last7days").end
                  )}
                </MenuItem>
                <MenuItem value="lastWeek" sx={{ fontSize: "0.65rem" }}>
                  Last Week{" "}
                  {formatDateRange(
                    getDateRangeForFilter("lastWeek").start,
                    getDateRangeForFilter("lastWeek").end
                  )}
                </MenuItem>
                <MenuItem value="thisMonth" sx={{ fontSize: "0.65rem" }}>
                  This Month{" "}
                  {formatDateRange(
                    getDateRangeForFilter("thisMonth").start,
                    getDateRangeForFilter("thisMonth").end
                  )}
                </MenuItem>
                <MenuItem value="lastMonth" sx={{ fontSize: "0.65rem" }}>
                  Last Month{" "}
                  {formatDateRange(
                    getDateRangeForFilter("lastMonth").start,
                    getDateRangeForFilter("lastMonth").end
                  )}
                </MenuItem>
                <MenuItem value="financialYear" sx={{ fontSize: "0.65rem" }}>
                  Financial Year{" "}
                  {formatDateRange(
                    getDateRangeForFilter("financialYear").start,
                    getDateRangeForFilter("financialYear").end
                  )}
                </MenuItem>
                <MenuItem value="custom" sx={{ fontSize: "0.65rem" }}>
                  Custom Range
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {showVehicleFilter && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "150px" },
              }}
            >
              <FormControl fullWidth size="small" sx={{ fontSize: "0.65rem" }}>
                <MultiSearchableAutocomplete
                  entityType="VEHICLE"
                  value={localSelectedVehicles}
                  onChange={(value) => null}
                  label="Vehicles"
                  onSelect={(value) => {
                    if (Array.isArray(value)) {
                      // Extract vehicle numbers from the array of objects
                      const vehicleNumbers = value
                        .map((item) =>
                          typeof item === "string"
                            ? item
                            : item.vehicleNumber || ""
                        )
                        .filter((vehicleNumber) => vehicleNumber);
                      setLocalSelectedVehicles(vehicleNumbers);
                    } else if (value && typeof value === "object") {
                      // Single object selected
                      const vehicleNumber = value.vehicleNumber || "";
                      if (vehicleNumber) {
                        setLocalSelectedVehicles([vehicleNumber]);
                      }
                    } else {
                      // null or other cases
                      setLocalSelectedVehicles([]);
                    }
                  }}
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : option.vehicleNumber || ""
                  }
                  renderOptionContent={(option) => (
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                        {option?.vehicleNumber}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.55rem",
                          color: "text.secondary",
                          backgroundColor: "grey.100",
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.5,
                        }}
                      >
                        {option?.acquisitionType}
                      </Typography>
                    </Box>
                  )}
                />
              </FormControl>
            </Box>
          )}

          {showVehicleIdFilter && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "150px" },
              }}
            >
              <FormControl fullWidth size="small" sx={{ fontSize: "0.65rem" }}>
                <MultiSearchableAutocomplete
                  entityType="VEHICLE"
                  value={localSelectedVehicleIds}
                  onChange={(value) => null}
                  label="Vehicles"
                  onSelect={(value) => {
                    if (Array.isArray(value)) {
                      // Extract IDs from the array of objects
                      const vehicleIds = value
                        .map((item) =>
                          typeof item === "string" ? item : item.id || ""
                        )
                        .filter((id) => id);
                      setLocalSelectedVehicleIds(vehicleIds);
                    } else if (value && typeof value === "object") {
                      // Single object selected
                      const vehicleId = value.id || "";
                      if (vehicleId) {
                        setLocalSelectedVehicleIds([vehicleId]);
                      }
                    } else {
                      // null or other cases
                      setLocalSelectedVehicleIds([]);
                    }
                  }}
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : option.vehicleNumber || ""
                  }
                  renderOptionContent={(option) => (
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                        {option?.vehicleNumber}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.55rem",
                          color: "text.secondary",
                          backgroundColor: "grey.100",
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.5,
                        }}
                      >
                        {option?.acquisitionType}
                      </Typography>
                    </Box>
                  )}
                />
              </FormControl>
            </Box>
          )}

          {showPartyIdFilter && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "150px" },
              }}
            >
              <FormControl fullWidth size="small" sx={{ fontSize: "0.65rem" }}>
                <MultiSearchableAutocomplete
                  entityType="PARTY"
                  value={localSelectedParties}
                  onChange={(value) => null}
                  label="Parties"
                  onSelect={(value) => {
                    if (Array.isArray(value)) {
                      // Extract IDs from the array of objects
                      const partyIds = value
                        .map((item) =>
                          typeof item === "string" ? item : item.id || ""
                        )
                        .filter((id) => id);
                      setLocalSelectedParties(partyIds);
                    } else if (value && typeof value === "object") {
                      // Single object selected
                      const partyId = value.id || "";
                      if (partyId) {
                        setLocalSelectedParties([partyId]);
                      }
                    } else {
                      // null or other cases
                      setLocalSelectedParties([]);
                    }
                  }}
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : option.partyName || option.name || ""
                  }
                />
              </FormControl>
            </Box>
          )}

          {showProviderFilter && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "150px" },
              }}
            >
              <FormControl fullWidth size="small" sx={{ fontSize: "0.65rem" }}>
                <TextField
                  fullWidth
                  label="Provider Name"
                  value={localProviderName}
                  onChange={(e) => setLocalProviderName(e.target.value)}
                  size="small"
                  inputProps={{ style: { fontSize: "0.65rem" } }}
                  InputLabelProps={{ style: { fontSize: "0.65rem" } }}
                />
              </FormControl>
            </Box>
          )}

          {showMechanicFilter && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "150px" },
              }}
            >
              <FormControl fullWidth size="small" sx={{ fontSize: "0.65rem" }}>
                <MultiSearchableAutocomplete
                  entityType="MECHANIC"
                  value={localMechanicName}
                  onChange={(value) => null}
                  label="Mechanics"
                  onSelect={(value) => {
                    if (Array.isArray(value)) {
                      // Extract names from the array of objects
                      const names = value
                        .map((item) =>
                          typeof item === "string" ? item : item.name || ""
                        )
                        .filter((name) => name);
                      handleMechanicChange(names);
                    } else if (value && typeof value === "object") {
                      // Single object selected
                      const name = value.name || "";
                      if (name) {
                        handleMechanicChange([name]);
                      }
                    } else {
                      // null or other cases
                      handleMechanicChange([]);
                    }
                  }}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.name || ""
                  }
                />
              </FormControl>
            </Box>
          )}

          {showGarageFilter && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "150px" },
              }}
            >
              <FormControl fullWidth size="small" sx={{ fontSize: "0.65rem" }}>
                <MultiSearchableAutocomplete
                  entityType="GARAGE"
                  value={localGarageName}
                  onChange={(value) => null}
                  label="Garages"
                  onSelect={(value) => {
                    if (Array.isArray(value)) {
                      // Extract names from the array of objects
                      const names = value
                        .map((item) =>
                          typeof item === "string" ? item : item.name || ""
                        )
                        .filter((name) => name);
                      handleGarageChange(names);
                    } else if (value && typeof value === "object") {
                      // Single object selected
                      const name = value.name || "";
                      if (name) {
                        handleGarageChange([name]);
                      }
                    } else {
                      // null or other cases
                      handleGarageChange([]);
                    }
                  }}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.name || ""
                  }
                />
              </FormControl>
            </Box>
          )}

          {showOperatorFilter && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "150px" },
                maxWidth: { sm: "250px" },
              }}
            >
              <FormControl fullWidth size="small" sx={{ fontSize: "0.65rem" }}>
                <MultiSearchableAutocomplete
                  entityType="SERVICE_OPERATOR"
                  value={localOperatorNames}
                  onChange={(value) => null}
                  label="Luggage Operators"
                  onSelect={(value) => {
                    if (Array.isArray(value)) {
                      // Extract names from the array of objects
                      const names = value
                        .map((item) =>
                          typeof item === "string" ? item : item.name || ""
                        )
                        .filter((name) => name);
                      handleOperatorChange(names);
                    } else if (value && typeof value === "object") {
                      // Single object selected
                      const name = value.name || "";
                      if (name) {
                        handleOperatorChange([name]);
                      }
                    } else {
                      // null or other cases
                      handleOperatorChange([]);
                    }
                  }}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.name || ""
                  }
                />
              </FormControl>
            </Box>
          )}

          {showPickupManFilter && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "150px" },
                maxWidth: { sm: "250px" },
              }}
            >
              <FormControl fullWidth size="small" sx={{ fontSize: "0.65rem" }}>
                <MultiSearchableAutocomplete
                  entityType="PICKUP_MAN"
                  value={localOperatorNames}
                  onChange={(value) => null}
                  label="Pickup Men"
                  onSelect={(value) => {
                    if (Array.isArray(value)) {
                      // Extract names from the array of objects
                      const names = value
                        .map((item) =>
                          typeof item === "string" ? item : item.name || ""
                        )
                        .filter((name) => name);
                      handleOperatorChange(names);
                    } else if (value && typeof value === "object") {
                      // Single object selected
                      const name = value.name || "";
                      if (name) {
                        handleOperatorChange([name]);
                      }
                    } else {
                      // null or other cases
                      handleOperatorChange([]);
                    }
                  }}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.name || ""
                  }
                />
              </FormControl>
            </Box>
          )}

          {showDriverCleanerFilter && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "150px" },
              }}
            >
              <FormControl fullWidth size="small" sx={{ fontSize: "0.65rem" }}>
                <TextField
                  fullWidth
                  label="Driver/Cleaner Name"
                  value={localDriverCleanerName}
                  onChange={(e) => setLocalDriverCleanerName(e.target.value)}
                  size="small"
                  inputProps={{ style: { fontSize: "0.65rem" } }}
                  InputLabelProps={{ style: { fontSize: "0.65rem" } }}
                />
              </FormControl>
            </Box>
          )}

          {showLocationFilter && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: "150px" },
                }}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ fontSize: "0.65rem" }}
                >
                  <Autocomplete
                    freeSolo
                    options={uniqueStops}
                    value={localPickupLocation}
                    getOptionLabel={(option) => getLocationOption(option)}
                    renderOption={(props, option) => (
                      <MenuItem
                        sx={{ fontSize: "0.65rem" }}
                        {...props}
                        key={option.stopName}
                      >
                        {option.stopName}
                      </MenuItem>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Pickup Location"
                        onChange={(e) => {
                          setLocalPickupLocation(e.target.value);
                        }}
                        size="small"
                        sx={{
                          ...commonTextFieldProps.sx,
                          fontSize: "0.65rem",
                          "& .MuiInputLabel-root": { fontSize: "0.65rem" },
                          "& .MuiInputBase-input": { fontSize: "0.65rem" },
                        }}
                      />
                    )}
                    onChange={(e, value) => {
                      if (typeof value === "string") {
                        setLocalPickupLocation(value);
                      } else if (value && value.stopName) {
                        setLocalPickupLocation(value.stopName);
                      } else {
                        setLocalPickupLocation("");
                      }
                    }}
                    onInputChange={(e, value) =>
                      setLocalPickupLocation(value || "")
                    }
                  />
                </FormControl>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: "150px" },
                }}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ fontSize: "0.65rem" }}
                >
                  <Autocomplete
                    freeSolo
                    options={uniqueStops}
                    value={localDropLocation}
                    getOptionLabel={(option) => getLocationOption(option)}
                    renderOption={(props, option) => (
                      <MenuItem
                        sx={{ fontSize: "0.65rem" }}
                        {...props}
                        key={option.stopName}
                      >
                        {option.stopName}
                      </MenuItem>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Drop Location"
                        onChange={(e) => {
                          setLocalDropLocation(e.target.value);
                        }}
                        size="small"
                        sx={{
                          ...commonTextFieldProps.sx,
                          fontSize: "0.65rem",
                          "& .MuiInputLabel-root": { fontSize: "0.65rem" },
                          "& .MuiInputBase-input": { fontSize: "0.65rem" },
                        }}
                      />
                    )}
                    onChange={(e, value) => {
                      if (typeof value === "string") {
                        setLocalDropLocation(value);
                      } else if (value && value.stopName) {
                        setLocalDropLocation(value.stopName);
                      } else {
                        setLocalDropLocation("");
                      }
                    }}
                    onInputChange={(e, value) =>
                      setLocalDropLocation(value || "")
                    }
                  />
                </FormControl>
              </Box>
            </>
          )}

          {showProviderIdFilter && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "150px" },
              }}
            >
              <FormControl fullWidth size="small" sx={{ fontSize: "0.65rem" }}>
                <MultiSearchableAutocomplete
                  entityType="PETROL_PUMP"
                  value={localSelectedProviderIds}
                  onChange={(value) => null}
                  label="Providers"
                  onSelect={(value) => {
                    if (Array.isArray(value)) {
                      // Extract IDs from the array of objects
                      const providerIds = value
                        .map((item) =>
                          typeof item === "string" ? item : item.id || ""
                        )
                        .filter((id) => id);
                      setLocalSelectedProviderIds(providerIds);
                    } else if (value && typeof value === "object") {
                      // Single object selected
                      const providerId = value.id || "";
                      if (providerId) {
                        setLocalSelectedProviderIds([providerId]);
                      }
                    } else {
                      // null or other cases
                      setLocalSelectedProviderIds([]);
                    }
                  }}
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : option.petrolPumpName || option.name || ""
                  }
                  renderOptionContent={(option) => (
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        fontSize={12}
                      >
                        {option.petrolPumpName}
                      </Typography>
                      {option.location && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontSize={10}
                        >
                          {option.location}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              </FormControl>
            </Box>
          )}

          {showProjectIdFilter && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "150px" },
              }}
            >
              <FormControl fullWidth size="small" sx={{ fontSize: "0.65rem" }}>
                <MultiSearchableAutocomplete
                  entityType="PROJECT"
                  value={localSelectedProjectIds}
                  onChange={(value) => null}
                  label={PROJECT_TERMINOLOGY.PLURAL}
                  onSelect={(value) => {
                    if (Array.isArray(value)) {
                      // Extract IDs from the array of objects
                      const projectIds = value
                        .map((item) =>
                          typeof item === "string" ? item : item.id || ""
                        )
                        .filter((id) => id);
                      setLocalSelectedProjectIds(projectIds);
                    } else if (value && typeof value === "object") {
                      // Single object selected
                      const projectId = value.id || "";
                      if (projectId) {
                        setLocalSelectedProjectIds([projectId]);
                      }
                    } else {
                      // null or other cases
                      setLocalSelectedProjectIds([]);
                    }
                  }}
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : option.name || option.projectName || ""
                  }
                  renderOptionContent={(option) => (
                    <Box p={0}>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        fontSize={12}
                      >
                        {option.name}
                      </Typography>
                      {option.client?.clientName && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontSize={10}
                        >
                          {option.client.clientName}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              </FormControl>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexShrink: 0,
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "flex-end", sm: "flex-start" },
            mt: { xs: 1, sm: 0 },
          }}
        >
          <Button
            variant="contained"
            onClick={handleApplyFilters}
            disabled={isApplyingFilters}
            {...smallButtonProp}
          >
            {isApplyingFilters && (
              <CircularProgress
                size={"0.65rem"}
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  marginLeft: "-7px",
                  marginTop: "-7px",
                }}
              />
            )}
            <span
              style={{
                opacity: isApplyingFilters ? 0 : 1,
                fontSize: "0.65rem",
              }}
            >
              Apply
            </span>
          </Button>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            disabled={isApplyingFilters}
            {...smallButtonProp}
          >
            <span style={{ fontSize: "0.65rem" }}>Clear Filters</span>
          </Button>
        </Box>
      </Box>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}
      >
        {localTimeFilter === "custom" && (
          <Grid container item xs={12} md={12} spacing={1}>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={localCustomStartDate}
                onChange={(e) => setLocalCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                inputProps={{ style: { fontSize: "12px" } }}
                sx={{
                  fontSize: "0.75rem",
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={localCustomEndDate}
                onChange={(e) => setLocalCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                inputProps={{ style: { fontSize: "12px" } }}
                sx={{
                  fontSize: "0.75rem",
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Grid>
          </Grid>
        )}

        {showMechanicFilter && localMechanicName.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {localMechanicName.map((mechanic) => (
                <Chip
                  key={mechanic}
                  label={mechanic}
                  onDelete={() => {
                    setLocalMechanicName((prev) =>
                      prev.filter((v) => v !== mechanic)
                    );
                  }}
                  deleteIcon={<CloseIcon />}
                  size="small"
                  sx={{ fontSize: "12px" }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showGarageFilter && localGarageName.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {localGarageName.map((garage) => (
                <Chip
                  key={garage}
                  label={garage}
                  onDelete={() => {
                    setLocalGarageName((prev) =>
                      prev.filter((v) => v !== garage)
                    );
                  }}
                  deleteIcon={<CloseIcon />}
                  size="small"
                  sx={{ fontSize: "12px" }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showVehicleFilter && localSelectedVehicles.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {localSelectedVehicles.map((vehicle) => (
                <Chip
                  key={vehicle}
                  label={vehicle}
                  onDelete={() => handleRemoveVehicle(vehicle)}
                  deleteIcon={<CloseIcon />}
                  size="small"
                  sx={{ fontSize: "12px" }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showVehicleIdFilter && localSelectedVehicleIds.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {localSelectedVehicleIds.map((vehicleId) => (
                <Chip
                  key={vehicleId}
                  label={getVehicleNumberById(vehicleId)}
                  onDelete={() => handleRemoveVehicleId(vehicleId)}
                  deleteIcon={<CloseIcon />}
                  size="small"
                  sx={{ fontSize: "12px" }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showPartyIdFilter && localSelectedParties.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {localSelectedParties.map((partyId) => (
                <Chip
                  key={partyId}
                  label={getPartyNameById(partyId)}
                  onDelete={() => {
                    setLocalSelectedParties((prev) =>
                      prev.filter((v) => v !== partyId)
                    );
                  }}
                  deleteIcon={<CloseIcon />}
                  size="small"
                  sx={{ fontSize: "12px" }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showProviderIdFilter && localSelectedProviderIds.length > 0 && (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, width: "20%" }}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {localSelectedProviderIds.map((providerId) => (
                <Chip
                  key={providerId}
                  label={getProviderNameById(providerId)}
                  onDelete={() => {
                    setLocalSelectedProviderIds((prev) =>
                      prev.filter((v) => v !== providerId)
                    );
                  }}
                  deleteIcon={<CloseIcon />}
                  size="small"
                  sx={{ fontSize: "12px" }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showProjectIdFilter && localSelectedProjectIds.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {localSelectedProjectIds.map((projectId) => (
                <Chip
                  key={projectId}
                  label={getProjectNameById(projectId)}
                  onDelete={() => {
                    setLocalSelectedProjectIds((prev) =>
                      prev.filter((v) => v !== projectId)
                    );
                  }}
                  deleteIcon={<CloseIcon />}
                  size="small"
                  sx={{ fontSize: "12px" }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showOperatorFilter && localOperatorNames.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {localOperatorNames.map((operator) => (
                <Chip
                  key={operator}
                  label={operator}
                  onDelete={() => handleRemoveOperator(operator)}
                  deleteIcon={<CloseIcon />}
                  size="small"
                  sx={{ fontSize: "12px" }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showPickupManFilter && localOperatorNames.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {localOperatorNames.map((pickupMan) => (
                <Chip
                  key={pickupMan}
                  label={pickupMan}
                  onDelete={() => handleRemoveOperator(pickupMan)}
                  deleteIcon={<CloseIcon />}
                  size="small"
                  sx={{ fontSize: "12px" }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ReportFilter;
